// Prompt: per-item compliance judgment (PRD Feature 3).
// Promotes pattern-layer "located / not_located" to "reported / partial / missing / not_applicable"
// with verbatim evidence excerpt + one-sentence rationale.

import type { ChatRequest } from '../types';
import type { ChecklistItem } from '../../checklists/types';
import type { ItemEvidence } from '../../patterns/types';
import type { ManuscriptDocument } from '../../pdf/types';

export const PROMPT_VERSION = 'judge_checklist_item-1.0';

const STATUSES = ['reported', 'partial', 'missing', 'not_applicable'] as const;

const SCHEMA = {
  type: 'object',
  additionalProperties: false,
  required: ['status', 'evidence_span', 'rationale'],
  properties: {
    status: { type: 'string', enum: STATUSES },
    evidence_span: { type: 'string', maxLength: 600 },
    rationale: { type: 'string', minLength: 1, maxLength: 400 },
  },
} as const;

export interface JudgeChecklistOutput {
  status: (typeof STATUSES)[number];
  evidence_span: string;
  rationale: string;
}

/** Bound the section snippet to keep prompts under context limits. */
function relevantContext(doc: ManuscriptDocument, item: ChecklistItem, evidence?: ItemEvidence): string {
  const sectionName = item.section_hint ?? 'methods';
  const span = doc.sections.find((s) => s.section === sectionName);
  const baseText = span ? doc.rawText.slice(span.start, span.end) : doc.rawText;

  // If pattern matched something, anchor the window around the first match.
  const firstMatch = evidence?.matches[0];
  if (firstMatch) {
    const anchor = firstMatch.offset;
    const start = Math.max(0, anchor - 800);
    const end = Math.min(doc.rawText.length, anchor + 1200);
    return doc.rawText.slice(start, end);
  }
  // No pattern match: send up to 3000 chars of the hinted section.
  return baseText.slice(0, 3000);
}

export function buildPrompt(
  doc: ManuscriptDocument,
  item: ChecklistItem,
  evidence: ItemEvidence | undefined,
  guidelineName: string,
): ChatRequest {
  const systemPrompt = `You are an editorial assistant grading a manuscript against the ${guidelineName} reporting guideline. For one checklist item, judge whether the manuscript reports it. Be conservative: "reported" requires the item to be clearly addressed; "partial" means the topic is mentioned but a key detail is missing; "missing" means no relevant evidence; "not_applicable" only when the item doesn't apply to this study design. Quote a brief verbatim span from the manuscript as evidence.`;

  const userPrompt = `Checklist item ${item.id}: ${item.text}\n\nPattern-layer status: ${evidence?.status ?? 'not_located'}${
    evidence?.matches.length
      ? ` (best match in §${evidence.matches[0].section}: "${evidence.matches[0].text.slice(0, 200)}")`
      : ''
  }\n\nRelevant manuscript passage:\n${relevantContext(doc, item, evidence)}\n\nReturn JSON: { "status": "reported"|"partial"|"missing"|"not_applicable", "evidence_span": <≤200-char verbatim quote or empty>, "rationale": <one sentence> }`;

  return {
    systemPrompt,
    userPrompt,
    schema: SCHEMA,
    maxTokens: 400,
    promptVersion: PROMPT_VERSION,
  };
}

export function parseResponse(json: unknown): JudgeChecklistOutput | null {
  if (typeof json !== 'object' || json === null) return null;
  const o = json as Record<string, unknown>;
  if (
    typeof o.status !== 'string' ||
    !STATUSES.includes(o.status as (typeof STATUSES)[number]) ||
    typeof o.evidence_span !== 'string' ||
    typeof o.rationale !== 'string'
  ) {
    return null;
  }
  return {
    status: o.status as JudgeChecklistOutput['status'],
    evidence_span: o.evidence_span,
    rationale: o.rationale,
  };
}
